import { useEffect, useId, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, LoaderCircle, Search } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../atoms/button';
import { Input } from '../../atoms/input';
import { AsyncComboboxOption, AsyncComboboxProps } from './interface';

export function AsyncCombobox(props: AsyncComboboxProps) {
  const {
    options,
    search,
    onSearchChange,
    displayValue,
    header,
    onLoadMore,
    onRetry,
    placeholder = 'Select an option...',
    searchPlaceholder = 'Search...',
    emptyMessage = 'No matching options.',
    disabled = false,
    isLoading = false,
    isFetching = false,
    isFetchingMore = false,
    hasMore = false,
    hasError = false,
    className,
  } = props;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const findOption = (optionValue: string, fallback?: AsyncComboboxOption[]) =>
    options.find((option) => option.value === optionValue)
      ?? fallback?.find((option) => option.value === optionValue);

  const isSelected = (optionValue: string) =>
    props.multiple ? props.value.includes(optionValue) : props.value === optionValue;

  let triggerLabel: string | undefined;
  if (props.multiple) {
    if (props.value.length === 1) {
      triggerLabel = findOption(props.value[0], props.selectedOptions)?.label ?? '1 selected';
    } else if (props.value.length > 1) {
      triggerLabel = `${props.value.length} selected`;
    }
  } else {
    triggerLabel = findOption(props.value, props.selectedOption ? [props.selectedOption] : undefined)?.label;
  }
  triggerLabel = displayValue ?? triggerLabel;

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  const select = (optionValue: string) => {
    if (props.multiple) {
      props.onValueChange(
        props.value.includes(optionValue)
          ? props.value.filter((v) => v !== optionValue)
          : [...props.value, optionValue],
      );
    } else {
      props.onValueChange(optionValue);
      setOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp' && options.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && options[activeIndex]) {
      event.preventDefault();
      if (!options[activeIndex].disabled) select(options[activeIndex].value);
    }
  };

  return (
    // Modal so the list stays wheel-scrollable when opened inside a Dialog.
    <PopoverPrimitive.Root open={open && !disabled} onOpenChange={setOpen} modal>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className={cn('truncate', !triggerLabel && 'text-muted-foreground')}>
            {triggerLabel ?? placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-72 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
            {header}
            <div className="relative p-1">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="pl-9 pr-9"
              />
              {isFetching && !isFetchingMore && (
                <LoaderCircle className="absolute right-3 top-3.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable={props.multiple || undefined}
              className="visible-scrollbar h-60 overflow-y-auto p-1"
              onScroll={(event) => {
                const element = event.currentTarget;
                if (hasMore && !isFetchingMore && element.scrollHeight - element.scrollTop - element.clientHeight < 40) {
                  onLoadMore?.();
                }
              }}
            >
              {isLoading ? (
                <div className="space-y-2 p-2" aria-label="Loading options">
                  {[0, 1, 2, 3].map((row) => <div key={row} className="h-9 animate-pulse rounded bg-muted" />)}
                </div>
              ) : hasError ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
                  <p className="text-sm text-muted-foreground">Unable to load options.</p>
                  <Button type="button" variant="outline" size="sm" onClick={onRetry}>Retry</Button>
                </div>
              ) : options.length === 0 ? (
                <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  {options.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected(option.value)}
                      aria-disabled={option.disabled || undefined}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => { if (!option.disabled) select(option.value); }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm',
                        index === activeIndex && 'bg-accent text-accent-foreground',
                        option.disabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      {props.multiple ? (
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                            isSelected(option.value) && 'bg-primary text-primary-foreground',
                          )}
                        >
                          <Check className={cn('h-3 w-3', !isSelected(option.value) && 'invisible')} />
                        </span>
                      ) : (
                        <Check className={cn('h-4 w-4 shrink-0', !isSelected(option.value) && 'invisible')} />
                      )}
                      <span className="min-w-0">
                        <span className="block truncate">{option.label}</span>
                        {option.description && (
                          <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                        )}
                      </span>
                    </button>
                  ))}
                  {isFetchingMore && (
                    <div className="flex justify-center py-3">
                      <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </>
              )}
            </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export type {
  AsyncComboboxOption,
  AsyncComboboxProps,
  AsyncComboboxSingleProps,
  AsyncComboboxMultipleProps,
} from './interface';